import { Controller } from '@hotwired/stimulus'
import ApexCharts from 'apexcharts'

export default class extends Controller {
  static values = { data: Array, name: String, categories: Array }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  initializeChart() {
    if (this.chart) {
      this.chart.destroy()
    }

    const colorMap = {
      'Plaza Green': '#34d399',
      'Plaza Red': '#f87171',
      'Não se aplica': '#fbbf24',
    }

    const palette = [
      '#fbbf24', '#60a5fa', '#6ee7b7', '#fdba74',
      '#fca5a5', '#c4b5fd', '#a5b4fc', '#94a3b8',
      '#f9a8d4', '#5eead4', '#d6d3d1',
    ]

    const labelColors =
      this.nameValue === 'Score de Crédito'
        ? this.categoriesValue.map((label) => colorMap[label] || '#94a3b8')
        : palette

    const truncateLabel = (label, maxLength = 25) => {
      if (label.length <= maxLength) return label
      return label.substring(0, maxLength) + '...'
    }

    const truncatedLabels = this.categoriesValue.map((label) => truncateLabel(label))
    const total = this.dataValue.reduce((a, b) => a + b, 0)

    const options = {
      colors: labelColors,
      chart: {
        type: 'donut',
        toolbar: { show: false },
        height: '100%',
        parentHeightOffset: 0,
        fontFamily: 'Inter, system-ui, sans-serif',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 600,
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            this.handleChartClick(event, chartContext, config)
          },
        },
      },
      title: {
        text: this.nameValue,
        align: 'center',
        style: {
          fontSize: '16px',
          fontWeight: '600',
          color: '#1f2937',
        },
      },
      series: this.dataValue,
      labels: truncatedLabels,
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '13px',
                fontWeight: 500,
                color: '#9ca3af',
                formatter: () => total,
              },
              name: {
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                offsetY: -4,
              },
              value: {
                fontSize: '20px',
                fontWeight: 700,
                color: '#1f2937',
                offsetY: 4,
              },
            },
          },
          expandOnClick: true,
        },
      },
      stroke: {
        width: 3,
        colors: ['#fff'],
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '13px',
          fontWeight: '600',
          colors: ['#fff'],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 0,
          blur: 2,
          opacity: 0.3,
        },
      },
      legend: {
        position: 'right',
        fontSize: '13px',
        fontWeight: 500,
        labels: { colors: '#6b7280' },
        markers: {
          size: 8,
          shape: 'circle',
          offsetX: -4,
        },
        itemMargin: { vertical: 4 },
      },
      tooltip: {
        custom: ({ series, seriesIndex }) => {
          const category = this.categoriesValue[seriesIndex]
          const value = series[seriesIndex]
          const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0
          const isFilterable = this.nameValue === 'Origem do Cliente' || this.nameValue === 'Campanhas'
          const clickHint = isFilterable
            ? '<div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">Clique para filtrar</div>'
            : ''

          return `<div style="padding: 8px 12px; font-family: Inter, system-ui, sans-serif; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.12);">
            <div style="font-weight: 600; color: #1f2937; margin-bottom: 2px;">${category}</div>
            <div style="color: #6b7280;"><span style="font-weight: 600; color: #374151;">${value}</span> clientes · ${pct}%</div>
            ${clickHint}
          </div>`
        },
      },
      states: {
        hover: {
          filter: { type: 'darken', value: 0.92 },
        },
      },
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  handleChartClick(_event, _chartContext, config) {
    const filterParam = {
      'Origem do Cliente': 'origin_filter',
      'Campanhas': 'campaign_filter',
    }[this.nameValue]

    if (!filterParam) return

    const dataPointIndex = config.dataPointIndex
    if (dataPointIndex >= 0 && dataPointIndex < this.categoriesValue.length) {
      const selected = this.categoriesValue[dataPointIndex]
      const url = new URL(window.location)
      url.searchParams.set(filterParam, selected)
      window.location.href = url.toString()
    }
  }
}
